/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
    bool, number, object, shape,
    string,
} from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import ReactHtmlParser from 'react-html-parser';
import QueryString from 'qs';
import {  useLocation } from 'react-router-dom';
import { Grid } from '@material-ui/core';
import DynamicPrice from './DynamicPrice';
import { getEnterpriseId } from '../../../../../../state/ducks/Member/ducks/EnterpriseId/EnterpriseId-Selectors';
import { CrossMerch } from '../../../CrossMerchContainer/CrossMerchContainer';
import { getIsAuthenticatedStatus, getIsPassportUserLoggedIn } from '../../../../../../state/ducks/Member/ducks/Common/Common-Selectors';
import { getPassportData } from '../../../../../../state/ducks/Passport/Passport-Selectors';
import validatePassport from '../../../../../helpers/validatePassport';
import { getPassportSubscriptionStatus } from '../../../../../../state/ducks/Common/Common-Selectors';
import { getIsBot, getUserSubmittedProductFilterZipcode } from '../../../../../../state/ducks/App/App-Selectors';
import { transformCharacters } from '../../../../../helpers/markdown/characterTransformer';
import ProductReview from '../ProductReview';
import { getBaseCode } from '../../../../../helpers/tracking/product/dataLayerProductHelpers';
import { skuDataType } from '../common/types';
import AddonIcon from './AddonIcon';

const useStyles = makeStyles((theme) => ({
    salePrice: {
        color: theme.palette.pdp.salePrice || '#a1001a',
        margin: '0 0 0 10px',
        fontSize: '20px',
        position: 'absolute',
        right: '0px',
        top: '0px',
    },
    selectedSalePriceInline: {
        position: 'absolute',
        right: '0px',
        top: '0px',
        fontWeight: 'bold',
        fontSize: '20px',
        [theme.breakpoints.down('sm')]: {
            fontSize: '.9em',
        },
        [theme.breakpoints.down('xs')]: {
            position: 'unset',
            fontSize: '1em',
            fontWeight: '700',
        },
    },
    discountAmount: {
        color: theme.palette.cms?.skuPriceDiscountColor,
        fontWeight: 'normal',
        marginLeft: '10px',
        position: 'absolute',
        top: '26px',
        right: '0px',
        fontSize: '18px',
        [theme.breakpoints.down('sm')]: {
            fontSize: '.9em',
        },
        [theme.breakpoints.down('xs')]: {
            position: 'relative',
            top: '0px',
        },
    },
    retailPrice: {
        fontSize: '20px',
        color: theme.palette.common?.black,
    },
    selectedRetailPrice: {
        fontWeight: 'bold',
        color: '#000000',
        fontSize: '24px',
        lineHeight: '25px',
        letterSpacing: '0.5px',
    },

    productHeaderWrapper: {
        display: 'block',
        position: 'relative',
        padding: '0px 16px',
        '@media screen and (max-width: 767px) and (min-width:600px)': {
            padding: '0px 4px 0px 0px',
        },
    },
    upsellNewTitle: {
        color: '#000000',
        fontSize: '28px',
        lineHeight: '29px',
        letterSpacing: '0.5px',
        margin: '12px 0px 8px',
        fontWeight: 'bold',
        '@media screen and (max-width: 767px) and (min-width:600px)': {
            margin: '0px 0px 8px',
        },
    },
    price: {
        textAlign: 'left',
        marginBottom: '11px',
    },
    productSkuNew: {
        fontStyle: 'normal',
        fontWeight: 'normal',
        textTransform: 'capitalize',
        fontSize: '20px',
        lineHeight: '25px',
        letterSpacing: '0.5px',
        marginBottom: '16px',
    },
    viewPriceBox: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'baseline',
        justifyContent: 'space-between',
    },
    ratingContainer: {
        marginBottom: '16px',
    },
    wrapper: {
        '& $upsellNewTitle': {
            margin: '12px 0px 16px',
        },
    },
}));

const ProductTitleTop = ({
    product,
    isPdpRedesignEnabled,
    selectedSku,
    skuData,
    isPassportBundleProduct,
    productUrl,
    isClubProduct,
    skuCount,
    hasAddons,
    content,
}) => {
    const { featureFlags = {} } = useContext(CrossMerch);
    const location = useLocation();
    const classes = useStyles();
    const isPassportUser = useSelector(getIsPassportUserLoggedIn);
    const isAuthenticated = useSelector(getIsAuthenticatedStatus);
    const passportData = useSelector(getPassportData);
    const isPassportMember = validatePassport('isActivePassportUser', passportData);
    const isPassportAdded = useSelector(getPassportSubscriptionStatus);
    const enterpriseId = useSelector(getEnterpriseId);
    const userSubmittedProductFilterZipcode = useSelector(getUserSubmittedProductFilterZipcode);
    const isBot = useSelector(getIsBot);
    const isSubscriptionSku = (selectedSku?.subscriptionsList?.type?.length > 0);
    const addonsPresentation = content?.entries?.[0]?.add_ons_presentation || {};
    const [priceRule, setPriceRule] = useState(null);
    const [productSkuPrices, setProductSkuPrices] = useState([]);
    const [dynamicPriceIsLoading, setDynamicPriceLoading] = useState(false);

    const isShownDiscountAmount = featureFlags['is_dollar_off_shown_enabled'];
    const isAvailabilityAtcLeftAlign = featureFlags['is-availability-atc-left-aligned'];
    const isSubscriptionV2Price = featureFlags['is-pdp-subscription-enabled'] && isSubscriptionSku;
    const isZipDynamicPricingEnabled = featureFlags['is-zip-dynamic-pricing-enabled'];
    const isDynamicPricingEnabled = featureFlags['is-dynamic-pricing-enabled-pwa'];

    const searchTerms = QueryString.parse(location.search.replace(/^\?/, ''));
    const hasSubscriptionParam = searchTerms?.subscription?.toLowerCase && searchTerms.subscription.toLowerCase() === 'basic';

    let variantList = [];
    if ((isPassportMember && isPassportUser) || isPassportAdded) {
        variantList = [{
            name: 'CUSTOMER_TYPE',
            value: 'Passport',
        }];
    } else if (isAuthenticated) {
        variantList = [{
            name: 'CUSTOMER_TYPE',
            value: 'Registered',
        }];
    }
    const dynamicPriceInstance = new DynamicPrice(skuData, enterpriseId, isSubscriptionSku && hasSubscriptionParam, selectedSku?.subscriptionsList?.type?.[0] || '', featureFlags['is-price-engine-batching-enabled'], variantList);
    const {
        getPriceRules, getRetailPrice, getSalePrice, getRetailDisplayPrice,
    } = dynamicPriceInstance;

    useEffect(() => {
        if (isSubscriptionV2Price && !hasSubscriptionParam) {
            /**
                 * Cleaning up priceRules is user unchecked the checkbox
                 */
            // eslint-disable-next-line no-param-reassign
            selectedSku.priceRules = null;
            (skuData).forEach((sku) => {
                // eslint-disable-next-line no-param-reassign
                sku.priceRules = null;
            });
            setPriceRule(null);
            setProductSkuPrices([]);
        }

        if (isSubscriptionV2Price && hasSubscriptionParam) {
            setDynamicPriceLoading(true);
            dynamicPriceInstance.call(isZipDynamicPricingEnabled, userSubmittedProductFilterZipcode).then((productPrices) => {
                const priceRules = getPriceRules(selectedSku?.id);
                // eslint-disable-next-line no-param-reassign
                selectedSku.priceRules = priceRules;
                setPriceRule(priceRules);
                setProductSkuPrices(productPrices?.newPrices || []);
                setDynamicPriceLoading(false);
            }).catch(() => {
                setDynamicPriceLoading(false);
            });
        }
    }, [isSubscriptionV2Price, featureFlags, isSubscriptionSku, hasSubscriptionParam, userSubmittedProductFilterZipcode]);

    useEffect(() => {
        if (hasSubscriptionParam && isSubscriptionV2Price) {
            setDynamicPriceLoading(false);
            return;
        }
        if (hasSubscriptionParam) {
            setDynamicPriceLoading(false);
            return;
        }
        if (isBot || !isDynamicPricingEnabled) {
            setDynamicPriceLoading(false);
            return;
        }
        if (enterpriseId) {
            setDynamicPriceLoading(true);
            dynamicPriceInstance.call(isZipDynamicPricingEnabled, userSubmittedProductFilterZipcode).then((productPrices) => {
                const priceRules = getPriceRules(selectedSku?.id);
                // eslint-disable-next-line no-param-reassign
                selectedSku.priceRules = priceRules;
                setPriceRule(priceRules);
                setProductSkuPrices(productPrices?.newPrices || []);
                setDynamicPriceLoading(false);
            }).catch(() => {
                setDynamicPriceLoading(false);
            });
        }
    }, [
        setDynamicPriceLoading,
        setProductSkuPrices,
        featureFlags,
        isSubscriptionV2Price,
        hasSubscriptionParam,
        userSubmittedProductFilterZipcode,
        enterpriseId,
        isPassportAdded,
    ]);

    let priceDetails = [];
    const dynamicPriceList = (sku) => {
        if ((isSubscriptionV2Price || isDynamicPricingEnabled || isZipDynamicPricingEnabled) && productSkuPrices.length > 0) {
            const skuItemPriceList = (productSkuPrices || []).find((skuItem) => skuItem?.id === sku?.id)?.prices || [];
            const currentPriceList = JSON.parse(JSON.stringify(sku?.prices || []));
            priceDetails = currentPriceList.map((price) => {
                /**
         * Retail price update for standard products by dynamic price API, not updating for subscription products
         */
                if (price.type === 'retail' && skuItemPriceList.length > 0 && getRetailPrice(skuItemPriceList) && !(hasSubscriptionParam && !isDynamicPricingEnabled)) {
                    const retailPrice = getRetailPrice(skuItemPriceList);
                    const salePrice = getSalePrice(skuItemPriceList);
                    // eslint-disable-next-line no-param-reassign
                    price.value = retailPrice;
                    if ((retailPrice === salePrice) || retailPrice > salePrice) {
                        // eslint-disable-next-line no-param-reassign
                        price.value = getRetailDisplayPrice(skuItemPriceList);
                    }
                }

                /**
     * Used to get what would be the retail dynamic price to show as comparison to discounted subscription price
     */
                if (price.type === 'retail' && skuItemPriceList.length > 0 && getRetailDisplayPrice(skuItemPriceList) && (hasSubscriptionParam && isDynamicPricingEnabled)) {
                    // eslint-disable-next-line no-param-reassign
                    price.value = getRetailDisplayPrice(skuItemPriceList);
                }
                /**
     * Sale price update for standard products and subscription products by dynamic price API
     */
                if (price.type === 'sale' && skuItemPriceList.length > 0 && (isDynamicPricingEnabled || isZipDynamicPricingEnabled || hasSubscriptionParam) && getSalePrice(skuItemPriceList)) {
                    // eslint-disable-next-line no-param-reassign
                    price.value = getSalePrice(skuItemPriceList);
                }
                return price;
            });
            if (selectedSku?.id === sku?.id) {
                // eslint-disable-next-line no-param-reassign
                selectedSku.priceRules = priceRule;
            }
        }
        return priceDetails;
    };

    // Removing this design from any feature and abtest
    const pricesList = dynamicPriceList(selectedSku);
    const renderSkuPrice = (sku, clubPrice = false) => {
        const round = (value) => Number(`${Math.round(value * 10) / 10}`).toFixed(2);
        const priceNonSelected = clubPrice ? classes.clubPrice : classes.retailPrice;
        const priceSelected = clubPrice ? classes.clubPriceSelected : classes.selectedRetailPrice;
        if (sku?.prices?.[0] && sku?.prices?.[1]) {
            let productPrice = getRetailPrice(pricesList) > 0 ? getRetailPrice(pricesList) : Number((sku.prices[0].value).toFixed(2));
            let discountedProductPrice = getSalePrice(pricesList) > 0 ? getSalePrice(pricesList) : Number((sku.prices[1].value).toFixed(2));
            if (isPassportBundleProduct?.enable) {
                productPrice = Number((productPrice + isPassportBundleProduct?.prices?.value).toFixed(2));
                discountedProductPrice = Number((discountedProductPrice + isPassportBundleProduct?.prices?.value).toFixed(2));
            }
            return productPrice > discountedProductPrice
                ? (
                    <>
                        <s regularprice="">
                            ${productPrice}
                        </s>
                        <span className={selectedSku.id !== sku.id ? classes.salePrice : `${classes.salePrice} ${classes.selectedSalePriceInline}`} saleprice="">
                            ${discountedProductPrice}
                        </span>
                        {isShownDiscountAmount && round(productPrice - discountedProductPrice) > 0 && (
                            <span className={classes.discountAmount} discount="">
                                ${round(productPrice - discountedProductPrice)} Off
                            </span>
                        )}
                    </>
                )
                : (
                    <span className={selectedSku.id !== sku.id ? priceNonSelected : priceSelected} productprice="">
                        ${productPrice}
                    </span>

                );
        }
        return null;
    };

    let productName = selectedSku.name;
    if (isPassportBundleProduct?.enable) {
        if (productName?.length < 20) {
            if (isPassportBundleProduct?.skuText) {
                productName = `${productName} ${isPassportBundleProduct?.skuText}`;
            } else {
                productName += ' Bundle';
            }
        }
    }
    // Ensure the name is not updated unnecessarily
    const [transformedName, setTransformedName] = useState(productName);
    useEffect(() => {
        // The name styles kept glitching on hover, this ensures these functions are only called when the name changes
        setTransformedName(transformCharacters(ReactHtmlParser(productName)));
    }, [productName]);

    return (
        <div className={`${classes.productHeaderWrapper} ${skuCount <= 1 ? '' : classes.wrapper}`}>
            <div className={classes.upsellNewTitle}>
                {transformedName}
            </div>
            {skuCount <= 1
          && (
              <div className={classes.viewPriceBox}>
                  <div className={classes.price}>
                      {!dynamicPriceIsLoading && renderSkuPrice(selectedSku)}
                  </div>
              </div>
          )}
            <div className={classes.ratingContainer}>
                { featureFlags['is-pdp-reviews-enabled'] && featureFlags['is-power-review-enabled'] && (
                    <ProductReview
                        product={product}
                        selectedSku={selectedSku}
                        productUrl={productUrl}
                        isClubProduct={isClubProduct}
                        showUnderTitle
                        inline={isAvailabilityAtcLeftAlign}
                        isPdpRedesignEnabled={isPdpRedesignEnabled}
                    />
                )}
            </div>
            {hasAddons && <AddonIcon addonsPresentation={addonsPresentation} /> }
        </div>
    );
};

ProductTitleTop.propTypes = {
    product: object,
    isPdpRedesignEnabled: bool,
    selectedSku: object,
    isPassportBundleProduct: shape({
        prices: shape({
            value: number,
        }),
        enable: bool,
    }),
    skuData: skuDataType.isRequired,
    productUrl: string,
    skuCount: number,
    isClubProduct: bool,
    hasAddons: true,
    content: object.isRequired,
};

ProductTitleTop.defaultProps = {
    product: {},
    isPdpRedesignEnabled: false,
    selectedSku: {},
    isPassportBundleProduct: {},
    productUrl: '',
    isClubProduct: false,
    skuCount: 0,
    hasAddons: false,
};

export default ProductTitleTop;
